import React from 'react';

import { KpiCompany } from 'generated/graphql';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { routes } from 'routes';

import { useSnackbar } from 'hooks/useSnackbar';

import { kpiRoutes } from '../kpiRoutes';
import { useCompany, FilterForm } from '../hooks/useCompany';
import { useTemplates } from '../hooks/useTemplates';

export const useCompanyStatusPage = () => {
  const [assignInProgress, setAssignInProgress] = React.useState(false);
  const [selectedRows, setSelectedRows] = React.useState<KpiCompany[]>([]);
  const [modal, setModal] = React.useState({
    open: false,
    title: '',
    text: '',
    submitTitle: '',
    template: {
      id: '0',
      name: '',
    },
  });

  const { operations: tmplOperations, models: tmplModels } = useTemplates();
  const { models, operations } = useCompany();

  const methods = useForm<FilterForm>({
    mode: 'onChange',
  });
  const { control, getValues } = methods;
  const filterForm = useWatch({
    control,
  });

  const { addSnackbar } = useSnackbar();

  React.useEffect(() => {
    tmplOperations.loadTemplates();
    operations.loadCompanies();
  }, []);

  React.useEffect(() => {
    operations.handleFiltering(getValues());
  }, [filterForm]);

  const closeModal = () => {
    setModal({
      ...modal,
      open: false,
    });
  };

  const confirmTemplate = (item: { id: React.ReactText; name: string }) => {
    if (item.id === '-1') {
      if (selectedRows.find((row) => !row.templateId) || !selectedRows.length) {
        addSnackbar(
          'You cannot unassign a Template from a Company that does not have a Template assigned.',
        );
      } else {
        const template = tmplModels.templates.find(
          ({ id }) => id === selectedRows[0].templateId,
        );
        setModal({
          open: true,
          title: `Unassign Template ${template?.name}`,
          text: `Are you sure you want to unassign the ${template?.name} Template from the below companies?`,
          submitTitle: 'Unassign',
          template: {
            id: '-1',
            name: template?.name || '',
          },
        });
      }
    } else {
      const template = tmplModels.templates.find(({ id }) => id === item.id);
      if (selectedRows.length) {
        let text = `Are you sure you want to assign the ${template?.name} Template to the below companies?`;
        if (selectedRows.find((row: KpiCompany) => row.templateId)) {
          text += ` NOTE: Companies with a “*” already have a Template assigned. If a Company has KPI data entered
                  from a previous Template, the KPI’s data will be saved and reusable in future Template(s).`;
        }
        setModal({
          open: true,
          title: `Assign Template ${template?.name}`,
          text: text,
          submitTitle: 'Assign',
          template: {
            id: template?.id || '',
            name: template?.name || '',
          },
        });
      } else if (template) {
        addSnackbar(
          'Please select one or more Company checkboxes to assign Template.',
        );
      }
    }
  };

  const assignUnassignTemplate = async () => {
    closeModal();
    setAssignInProgress(true);
    if (modal.template.id?.toString() === '-1') {
      await operations.unassignTemplate(selectedRows);
      operations.loadCompanies();
      addSnackbar(`Companies Updated: ${modal.template.name} was unassigned.`);
    } else {
      await operations.assignTemplate(selectedRows, modal.template.id);
      operations.loadCompanies();
      addSnackbar(`Companies Updated: ${modal.template.name} was assigned.`);
    }
    setAssignInProgress(false);
  };

  const columns = [
    {
      Header: 'Company',
      accessor: 'name',
      Cell: (cellData: any) => {
        const { value, row } = cellData;
        const { original } = row;
        return <Link to={`/kpis/data-entry/${original.id}`}>{value}</Link>;
      },
      sortType: 'ialphanumeric',
    },
    {
      Header: 'Template',
      accessor: 'templateId',
      Cell: ({ value }: { value: string }) => {
        const template = tmplModels.templates.find(
          (template) => template.id === value,
        );
        if (template) {
          return (
            <Link
              to={{
                pathname: routes.KPI + `${kpiRoutes.Templates}`,
                state: { templateId: value },
              }}
            >
              {template?.name}
            </Link>
          );
        } else return '-';
      },
    },
    {
      Header: 'Status',
      accessor: 'lastStatus',
      Cell: ({ value }: { value: { status: string } }) => {
        if (value)
          switch (value.status) {
            case 'DRAFT':
              return 'Draft Saved';
            case 'PUBLISHED':
              return 'Published';
            case 'HISTORY':
              return 'History';
          }
        return '-';
      },
    },
    {
      Header: 'Updated By',
      accessor: (originalRow: any) => {
        if (originalRow.lastStatus) {
          return moment(originalRow.lastStatus.date).toDate();
        }
        return new Date();
      },
      Cell: ({ row }: { row: any }) => {
        if (row.original.lastStatus) {
          const status = row.original.lastStatus;
          return (
            `${status.user.firstName} ${status.user.lastName} ` +
            moment(status.date).format('MM/DD/YYYY')
          );
        }
        return '-';
      },
      sortType: 'datetime',
    },
  ];

  return {
    assignInProgress,
    assignUnassignTemplate,
    closeModal,
    columns,
    confirmTemplate,
    methods,
    modal,
    models,
    selectedRows,
    setSelectedRows,
    tmplModels,
  };
};
